import * as signalR from "@microsoft/signalr";
import { HubConnectionBuilder, type HubConnectionState, type IHttpConnectionOptions } from "@microsoft/signalr";



import { Factory } from "@/data/dao/Factory";
import { OrderDao } from "@/data/dao/service/OrderDao";
import { AppSocket } from "@/data/dao/socket/AppSocket";
import { SocketEventHelper } from "@/data/dao/socket/SocketEventHelper";
import { SocketEventParser } from "@/data/dao/socket/SocketEventParser";
import { dataEvents } from "@/data/events/dataEvents";
import type { IFulfillmentPositionsChangedEvent, ILinkedFulfillmentPositionsChangedEvent, IOperatorLinkedFulfillmentsChangedEvent, IOrderOutstandingChangedEvent, IPayLaterOrderConfirmedEvent, IReservationTableAssignmentChangedEvent, IRoomPlanChangedEvent, ITableCovidUserCountChangedEvent } from "@/data/model/Events";
import { AuthTokenService } from "@/data/services/AuthTokenService";
import { config, loggingConfig } from "@/data/util/Config";
import { Logging } from "@/data/util/Logging";
import { Signal, TypedSignal } from "@/data/util/Signal";



import { logError } from "@/shared/logging/errors";


export class OperatorSocket {
    private hubConnection: signalR.HubConnection;
    private authTokenService: AuthTokenService;

    public connectionId: string | null | undefined;

    public onConnectionChanged: TypedSignal<HubConnectionState>;
    public onReconnected: Signal;

    constructor() {
        this.authTokenService = Factory.getInstance<AuthTokenService>(AuthTokenService);

        this.onConnectionChanged = new TypedSignal<HubConnectionState>();
        this.onReconnected = new Signal();

        const options: IHttpConnectionOptions = {
            accessTokenFactory: () => this.authTokenService.getBearer() ?? "",
        };
        this.hubConnection = new HubConnectionBuilder()
            .withUrl(!config.isMockClient ? "/socket/operator" : config.mockClientOperatorSocketAddress, options)
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: () => {
                    return 2000;
                },
            })
            .configureLogging(
                loggingConfig.enableSocketNotificationsLogging ? signalR.LogLevel.Debug : signalR.LogLevel.Critical
            )
            .build();

        this.hubConnection.onreconnecting(() => {
            Logging.LogSocketNotification(
                `On reconnecting id: ${this.hubConnection.connectionId}, state: ${this.hubConnection.state}, srv timeout: ${this.hubConnection.serverTimeoutInMilliseconds}, keepAlive: ${this.hubConnection.keepAliveIntervalInMilliseconds}`
            );
            this.onConnectionChanged.dispatch(this.hubConnection.state);
        });

        this.hubConnection.onclose(() => {
            Logging.LogSocketNotification(
                `On closed id: ${this.hubConnection.connectionId}, state: ${this.hubConnection.state}, srv timeout: ${this.hubConnection.serverTimeoutInMilliseconds}, keepAlive: ${this.hubConnection.keepAliveIntervalInMilliseconds}`
            );
            this.onConnectionChanged.dispatch(this.hubConnection.state);
        });

        this.hubConnection.onreconnected(() => {
            Logging.LogSocketNotification(
                `On reconnected id: ${this.hubConnection.connectionId}, state: ${this.hubConnection.state}, srv timeout: ${this.hubConnection.serverTimeoutInMilliseconds}, keepAlive: ${this.hubConnection.keepAliveIntervalInMilliseconds}`
            );
            this.connectionId = this.hubConnection.connectionId;
            this.onReconnected.dispatch();
            this.onConnectionChanged.dispatch(this.hubConnection.state);
        });

        this.hubConnection.on("TableCovidUserCountChanged", (ev) => {
            Logging.LogSocketNotification("OperatorSocket: TableCovidUserCountChanged");
            const eventData = OperatorSocket.parseTableCovidUserCountChangedEvent(ev);
            dataEvents.operatorSocket.TableCovidUserCountChanged.dispatch(eventData);
        });

        this.hubConnection.on("RoomPlanChanged", (ev) => {
            Logging.LogSocketNotification("OperatorSocket: RoomPlanChanged");
            const eventData = OperatorSocket.parseRoomPlanChangedEvent(ev);
            dataEvents.operatorSocket.RoomPlanChanged.dispatch(eventData);
        });

        this.hubConnection.on("OperatorUserSignedOut", () => {
            Logging.LogSocketNotification("OperatorSocket: OperatorUserSignedOut");
            dataEvents.operatorSocket.OperatorUserSignedOut.dispatch();
        });

        this.hubConnection.on("OnOrderOutstandingChanged", (ev) => {
            Logging.LogSocketNotification("OperatorSocket: OnOrderOutstandingChanged");
            const eventData = OperatorSocket.parseOrderOutstandingChangedEvent(ev);
            dataEvents.operatorSocket.OrderOutstandingChanged.dispatch(eventData);
        });

        this.hubConnection.on("OnPayLaterOrderConfirmed", (ev) => {
            Logging.LogSocketNotification("OperatorSocket: OnPayLaterOrderConfirmed");
            const eventData = OperatorSocket.parsePayLaterOrderConfirmedEvent(ev);
            dataEvents.operatorSocket.PayLaterOrderConfirmed.dispatch(eventData);
        });

        // Fulfillments

        this.hubConnection.on("OnFulfillmentChanged", (ev) => {
            Logging.LogSocketNotification("OperatorSocket: OnFulfillmentChanged");
            const eventData = SocketEventParser.parseCustomerFulfillmentChangedEvent(ev);

            if (SocketEventHelper.triggerFulfillmentDataEvent(eventData.fulfillmentGroupId)) {
                dataEvents.operatorSocket.FulfillmentChanged.dispatch(eventData);
            }
        });

        this.hubConnection.on("OnLinkedFulfillmentsChanged", (ev) => {
            Logging.LogSocketNotification("OperatorSocket: OnLinkedFulfillmentsChanged");
            const eventData = OperatorSocket.parseOperatorLinkedFulfillmentsChangedEvent(ev);

            if (eventData.fulfillmentGroupIds.find((fg) => SocketEventHelper.triggerFulfillmentDataEvent(fg)) != null) {
                dataEvents.operatorSocket.LinkedFulfillmentsChanged.dispatch();
            }
        });

        this.hubConnection.on("OnFulfillmentPositionsChanged", (ev) => {
            Logging.LogSocketNotification("OperatorSocket: OnFulfillmentPositionsChanged");
            const eventData = OperatorSocket.parseFulfillmentPositionsChangedEvent(ev);

            if (eventData.fulfillmentGroupIds.find((fg) => SocketEventHelper.triggerFulfillmentDataEvent(fg) != null)) {
                dataEvents.operatorSocket.FulfillmentPositionsChanged.dispatch(eventData);
            }
        });

        this.hubConnection.on("LinkedFulfillmentsPositionsChanged", (ev) => {
            Logging.LogSocketNotification("OperatorSocket: LinkedFulfillmentsPositionsChanged");
            const eventData = OperatorSocket.parseLinkedFulfillmentsPositionsChangedEvent(ev);

            if (eventData.fulfillmentGroupIds.find((fg) => SocketEventHelper.triggerFulfillmentDataEvent(fg)) != null) {
                dataEvents.operatorSocket.LinkedFulfillmentsPositionsChanged.dispatch(eventData);
            }
        });

        this.hubConnection.on("OrderCancelled", (ev) => {
            Logging.LogSocketNotification("OperatorSocket: OrderCancelled");
            const eventData = OrderDao.parseOrderCancelledEvent(ev);
            dataEvents.operatorSocket.OrderCancelled.dispatch(eventData);
        });

        this.hubConnection.on("BillOpened", (ev) => {
            Logging.LogSocketNotification("OperatorSocket: BillOpened");
            const eventData = AppSocket.parseBillOpenedEvent(ev);
            dataEvents.operatorSocket.BillOpened.dispatch(eventData);
        });

        this.hubConnection.on("BillChanged", (ev) => {
            Logging.LogSocketNotification("OperatorSocket: BillChanged");
            const eventData = AppSocket.parseBillChangedEvent(ev);
            dataEvents.operatorSocket.BillChanged.dispatch(eventData);
        });

        this.hubConnection.on("BillNameChanged", (ev) => {
            Logging.LogSocketNotification("OperatorSocket: BillNameChanged");
            const eventData = AppSocket.parseBillNameChangedEvent(ev);
            dataEvents.operatorSocket.BillNameChanged.dispatch(eventData);
        });

        this.hubConnection.on("BillManualEndUsersCountChanged", (ev) => {
            Logging.LogSocketNotification("OperatorSocket: BillManualEndUsersCountChanged");
            const eventData = AppSocket.parseBillManualEndUsersCountChangedEvent(ev);
            dataEvents.operatorSocket.BillManualEndUsersCountChanged.dispatch(eventData);
        });

        this.hubConnection.on("BillClosed", (ev) => {
            Logging.LogSocketNotification("OperatorSocket: BillClosed");
            const eventData = AppSocket.parseBillClosedEvent(ev);
            dataEvents.operatorSocket.BillClosed.dispatch(eventData);
        });

        this.hubConnection.on("TransferBillItemsDisabled", (ev) => {
            Logging.LogSocketNotification("OperatorSocket: TransferBillItemsDisabled");
            const eventData = AppSocket.parseBillEvent(ev);
            dataEvents.operatorSocket.TransferBillItemsDisabled.dispatch(eventData);
        });

        this.hubConnection.on("UserSessionExpired", () => {
            Logging.LogSocketNotification("OperatorSocket: UserSessionExpired");
            dataEvents.operatorSocket.UserSessionExpired.dispatch();
        });

        this.hubConnection.on("BillDiscountChanged", (ev) => {
            Logging.LogSocketNotification("OperatorSocket: BillDiscountChanged");
            const eventData = AppSocket.parseBillDiscountChangedEvent(ev);
            dataEvents.operatorSocket.BillDiscountChanged.dispatch(eventData);
        });

        this.hubConnection.on("BillDiscountChangedShared", (ev) => {
            Logging.LogSocketNotification("OperatorSocket: BillDiscountChangedShared");
            const eventData = AppSocket.parseBillDiscountChangedEvent(ev);
            dataEvents.operatorSocket.BillDiscountChanged.dispatch(eventData);
        });

        this.hubConnection.on("ReservationTableAssignmentChanged", (ev) => {
            Logging.LogSocketNotification("OperatorSocket: ReservationTableAssignmentChanged");
            const eventData = OperatorSocket.parseReservationTableAssignmentChanged(ev);
            dataEvents.operatorSocket.ReservationTableAssignmentChanged.dispatch(eventData);
        });
    }

    public async connect(): Promise<void> {
        if (config.isMockClient) return;

        try {
            await this.disconnect();
            await this.hubConnection.start();
            this.connectionId = this.hubConnection.connectionId;
            this.onConnectionChanged.dispatch(this.hubConnection.state);
            Logging.LogSocketNotification(
                `Connected id: ${this.hubConnection.connectionId}, state: ${this.hubConnection.state}, srv timeout: ${this.hubConnection.serverTimeoutInMilliseconds}, keepAlive: ${this.hubConnection.keepAliveIntervalInMilliseconds}`
            );
        } catch (ex) {
            logError(ex);
            this.onConnectionChanged.dispatch(this.hubConnection.state);
            this.connectionId = undefined;
            throw new Error("Could not connect socket");
        }
    }

    public async disconnect(): Promise<void> {
        if (config.isMockClient) return;

        this.connectionId = undefined;
        return await this.hubConnection.stop();
    }

    public async listenToLocation(locationId: string): Promise<void> {
        if (config.isMockClient) return;

        Logging.LogSocketNotification(
            `JoinLocationGroup id: ${this.hubConnection.connectionId}, state: ${this.hubConnection.state}, srv timeout: ${this.hubConnection.serverTimeoutInMilliseconds}, keepAlive: ${this.hubConnection.keepAliveIntervalInMilliseconds}`
        );
        await this.hubConnection.invoke("JoinLocationGroup", locationId);
    }

    public async checkConnectivity(): Promise<void> {
        Logging.LogSocketNotification(
            `Check connectivity id: ${this.hubConnection.connectionId}, state: ${this.hubConnection.state}, srv timeout: ${this.hubConnection.serverTimeoutInMilliseconds}, keepAlive: ${this.hubConnection.keepAliveIntervalInMilliseconds}`
        );
        this.onConnectionChanged.dispatch(this.hubConnection.state);
        if (
            this.hubConnection.state === signalR.HubConnectionState.Connected ||
            this.hubConnection.state === signalR.HubConnectionState.Connecting ||
            this.hubConnection.state === signalR.HubConnectionState.Reconnecting
        )
            return;

        Logging.LogSocketNotification(
            `Connection stop and start connection id: ${this.hubConnection.connectionId}, state: ${this.hubConnection.state}, srv timeout: ${this.hubConnection.serverTimeoutInMilliseconds}, keepAlive: ${this.hubConnection.keepAliveIntervalInMilliseconds}`
        );
        try {
            await this.connect();
        } catch (ex) {
            logError(ex);
            await this.connect();
        }
    }

    public static parseTableCovidUserCountChangedEvent(literal: any): ITableCovidUserCountChangedEvent {
        return {
            tableId: literal.tableId,
            locationId: literal.locationId,
            covidUsersAtTable: parseInt(literal.covidUsersAtTable),
        };
    }

    public static parseRoomPlanChangedEvent(literal: any): IRoomPlanChangedEvent {
        return {
            roomId: literal.roomId,
            locationId: literal.locationId,
        };
    }

    public static parseReservationTableAssignmentChanged(literal: any): IReservationTableAssignmentChangedEvent {
        return {
            assignedRoomId: literal.assignedRoomId,
            locationId: literal.locationId,
        };
    }

    private static parseOrderOutstandingChangedEvent(literal: any): IOrderOutstandingChangedEvent {
        return {
            identityId: literal.identityId == null ? undefined : literal.identityId,
            locationId: literal.locationId,
            orderId: literal.orderId,
            fulfillmentHandling: parseInt(literal.fulfillmentHandling),
            fulfillmentGroupIds: literal.fulfillmentGroupIds,
            orderState: parseInt(literal.orderState),
            fulfillmentPositionDisplayMode: parseInt(literal.fulfillmentPositionDisplayMode),
        };
    }

    private static parsePayLaterOrderConfirmedEvent(literal: any): IPayLaterOrderConfirmedEvent {
        return {
            identityId: literal.identityId,
            locationId: literal.locationId,
            orderId: literal.orderId,
            fulfillmentHandling: parseInt(literal.fulfillmentHandling),
            fulfillmentGroupIds: literal.fulfillmentGroupIds,
            fulfillmentPositionDisplayMode: parseInt(literal.fulfillmentPositionDisplayMode),
        };
    }

    private static parseFulfillmentPositionsChangedEvent(literal: any): IFulfillmentPositionsChangedEvent {
        return {
            locationId: literal.locationId,
            fulfillmentGroupIds: literal.fulfillmentGroupIds,
        };
    }

    private static parseLinkedFulfillmentsPositionsChangedEvent(literal: any): ILinkedFulfillmentPositionsChangedEvent {
        return {
            locationId: literal.locationId,
            fulfillmentGroupIds: literal.fulfillmentGroupIds,
        };
    }

    private static parseOperatorLinkedFulfillmentsChangedEvent(literal: any): IOperatorLinkedFulfillmentsChangedEvent {
        return {
            fulfillmentGroupIds: literal.fulfillmentGroupIds,
        };
    }
}
